/**
 * PDF theme colors and drawing helpers for jsPDF + autoTable report PDFs.
 *
 * Colors come from the site's `--c-primary-*` CSS variables (stored as "r, g, b"
 * in `styles/theme-colors.scss`), so PDFs follow the active theme.
 * @module pdfTheme
 */

/** Fallbacks matching `styles/theme-colors.scss`. */
const DEFAULT_PRIMARY = {
  50: [224, 246, 242],
  100: [193, 236, 228],
  200: [162, 227, 215],
  500: [56, 188, 164],
  600: [45, 150, 130],
  700: [38, 126, 111],
  800: [28, 94, 82],
  900: [30, 72, 60],
};

/**
 * Parses "r, g, b" or "r g b" into an RGB array.
 * @param {string} value
 * @returns {number[]|null}
 */
export const parseRgbVariable = (value) => {
  const parts = String(value || "")
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number);
  if (parts.length < 3 || parts.slice(0, 3).some((n) => !Number.isFinite(n))) {
    return null;
  }
  return parts.slice(0, 3);
};

/**
 * Reads a primary shade from the document, falling back to the default theme.
 * @param {number} shade - e.g. 600
 * @returns {number[]} [r, g, b]
 */
export const getPrimaryRgb = (shade) => {
  if (typeof window !== "undefined" && window.document?.documentElement) {
    const value = window
      .getComputedStyle(window.document.documentElement)
      .getPropertyValue(`--c-primary-${shade}`);
    const parsed = parseRgbVariable(value);
    if (parsed) return parsed;
  }
  return DEFAULT_PRIMARY[shade] || DEFAULT_PRIMARY[600];
};

/**
 * Theme colors for report PDFs.
 * @returns {{ primary: number[], primaryDark: number[], primaryLight: number[], primaryTint: number[], text: number[], muted: number[], border: number[], zebra: number[], negative: number[], white: number[] }}
 */
export const getPdfTheme = () => ({
  primary: getPrimaryRgb(600),
  primaryDark: getPrimaryRgb(800),
  primaryLight: getPrimaryRgb(100),
  primaryTint: getPrimaryRgb(50),
  text: [31, 41, 55],
  muted: [107, 114, 128],
  border: [229, 231, 235],
  zebra: [249, 250, 251],
  negative: [200, 30, 30],
  white: [255, 255, 255],
});

export const PDF_MARGIN = { top: 12, left: 8, right: 8, bottom: 14 };

/**
 * autoTable options shared by themed tables.
 * @param {ReturnType<typeof getPdfTheme>} theme
 * @returns {object}
 */
export const themedTableOptions = (theme) => ({
  theme: "grid",
  styles: {
    font: "helvetica",
    fontSize: 8,
    textColor: theme.text,
    lineColor: theme.border,
    lineWidth: 0.1,
    cellPadding: 1.6,
  },
  headStyles: {
    fillColor: theme.primary,
    textColor: theme.white,
    fontStyle: "bold",
    fontSize: 8,
  },
  alternateRowStyles: { fillColor: theme.zebra },
  margin: PDF_MARGIN,
});

/**
 * Starts a new page when fewer than `needed` mm remain.
 * @param {jsPDF} pdf
 * @param {number} y
 * @param {number} [needed=30]
 * @returns {number} y to draw at
 */
export const ensureSpace = (pdf, y, needed = 30) => {
  const pageHeight = pdf.internal.pageSize.getHeight();
  if (y + needed > pageHeight - PDF_MARGIN.bottom) {
    pdf.addPage();
    return PDF_MARGIN.top;
  }
  return y;
};

/**
 * Section title with a primary accent bar.
 * @param {jsPDF} pdf
 * @param {string} title
 * @param {number} y
 * @param {ReturnType<typeof getPdfTheme>} theme
 * @param {number} [x=PDF_MARGIN.left]
 * @returns {number} y below the title
 */
export const drawSectionTitle = (pdf, title, y, theme, x = PDF_MARGIN.left) => {
  pdf.setFillColor(...theme.primary);
  pdf.rect(x, y - 3.2, 1.2, 4.2, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(...theme.primaryDark);
  pdf.text(title, x + 3, y);
  pdf.setTextColor(...theme.text);
  return y + 3;
};

/**
 * Row of KPI cards (label above value).
 * @param {jsPDF} pdf
 * @param {Array<{ label: string, value: string, tone?: "positive"|"negative" }>} kpis
 * @param {number} y
 * @param {ReturnType<typeof getPdfTheme>} theme
 * @returns {number} y below the cards
 */
export const drawKpiCards = (pdf, kpis, y, theme) => {
  if (!kpis.length) return y;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const gap = 2.5;
  const width =
    (pageWidth - PDF_MARGIN.left - PDF_MARGIN.right - gap * (kpis.length - 1)) /
    kpis.length;
  const height = 14;

  kpis.forEach((kpi, index) => {
    const x = PDF_MARGIN.left + index * (width + gap);
    pdf.setFillColor(...theme.primaryTint);
    pdf.setDrawColor(...theme.primaryLight);
    pdf.roundedRect(x, y, width, height, 1.5, 1.5, "FD");
    pdf.setFillColor(...theme.primary);
    pdf.rect(x, y + 1.5, 0.9, height - 3, "F");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(...theme.muted);
    pdf.text(kpi.label.toUpperCase(), x + 3, y + 5);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10.5);
    pdf.setTextColor(
      ...(kpi.tone === "negative" ? theme.negative : theme.primaryDark)
    );
    pdf.text(kpi.value, x + 3, y + 11, { maxWidth: width - 4 });
  });

  pdf.setTextColor(...theme.text);
  return y + height + 6;
};

/**
 * Titled themed table.
 * @param {jsPDF} pdf
 * @param {object} params
 * @param {string} [params.title]
 * @param {string[]} params.head
 * @param {Array<Array<string>>} params.body
 * @param {number} params.y
 * @param {ReturnType<typeof getPdfTheme>} params.theme
 * @param {object} [params.options] - Extra autoTable options (merged last)
 * @returns {number} y below the table
 */
export const drawTableSection = (pdf, { title, head, body, y, theme, options = {} }) => {
  if (!body.length) return y;
  let startY = ensureSpace(pdf, y, 25);
  if (title) startY = drawSectionTitle(pdf, title, startY, theme);
  const base = themedTableOptions(theme);
  pdf.autoTable({
    ...base,
    startY,
    head: [head],
    body,
    ...options,
    styles: { ...base.styles, ...(options.styles || {}) },
    headStyles: { ...base.headStyles, ...(options.headStyles || {}) },
    margin: { ...base.margin, ...(options.margin || {}) },
  });
  return pdf.lastAutoTable.finalY + 7;
};

/**
 * Two titled tables side by side; returns y below the taller one.
 * Each side is `{ title, head, body, options }`. Empty sides are skipped.
 * @param {jsPDF} pdf
 * @param {Array<object>} sides - Up to two table definitions
 * @param {number} y
 * @param {ReturnType<typeof getPdfTheme>} theme
 * @param {object} [options]
 * @param {boolean} [options.keepHalfWidth=false] - A lone table stays in the left column
 * @returns {number}
 */
export const drawSideBySide = (pdf, sides, y, theme, { keepHalfWidth = false } = {}) => {
  const tables = sides.filter((side) => side && side.body?.length);
  if (!tables.length) return y;
  if (tables.length === 1 && !keepHalfWidth) {
    return drawTableSection(pdf, { ...tables[0], y, theme });
  }

  const startY = ensureSpace(pdf, y, 45);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const gap = 6;
  const columnWidth =
    (pageWidth - PDF_MARGIN.left - PDF_MARGIN.right - gap) / 2;
  const startPage = pdf.internal.getCurrentPageInfo().pageNumber;
  let maxY = startY;
  let maxPage = startPage;

  tables.slice(0, 2).forEach((side, index) => {
    pdf.setPage(startPage);
    const left = PDF_MARGIN.left + index * (columnWidth + gap);
    const tableY = side.title
      ? drawSectionTitle(pdf, side.title, startY, theme, left)
      : startY;
    const base = themedTableOptions(theme);
    pdf.autoTable({
      ...base,
      startY: tableY,
      head: [side.head],
      body: side.body,
      tableWidth: columnWidth,
      ...(side.options || {}),
      styles: { ...base.styles, ...(side.options?.styles || {}) },
      margin: { ...base.margin, left, right: pageWidth - left - columnWidth },
    });
    const endPage = pdf.internal.getCurrentPageInfo().pageNumber;
    const endY = pdf.lastAutoTable.finalY;
    if (endPage > maxPage || (endPage === maxPage && endY > maxY)) {
      maxPage = endPage;
      maxY = endY;
    }
  });

  pdf.setPage(maxPage);
  return maxY + 7;
};

/**
 * Label/value metric sections laid out two per row.
 * @param {jsPDF} pdf
 * @param {Array<{ title: string, rows: Array<{ label: string, value: string }> }>} sections
 * @param {number} y
 * @param {ReturnType<typeof getPdfTheme>} theme
 * @returns {number}
 */
export const drawMetricSections = (pdf, sections, y, theme) => {
  let nextY = y;
  for (let i = 0; i < sections.length; i += 2) {
    nextY = drawSideBySide(
      pdf,
      sections.slice(i, i + 2).map((section) => ({
        title: section.title,
        head: ["Metric", "Value"],
        body: section.rows.map((row) => [row.label, row.value]),
        options: {
          showHead: "never",
          columnStyles: {
            0: { textColor: theme.muted },
            1: { halign: "right", fontStyle: "bold" },
          },
        },
      })),
      nextY,
      theme,
      { keepHalfWidth: true }
    );
  }
  return nextY;
};

/**
 * Footer on every page: page number, company, report name, and a primary rule.
 * @param {jsPDF} pdf
 * @param {string} text
 * @param {ReturnType<typeof getPdfTheme>} theme
 */
export const drawPageFooters = (pdf, text, theme) => {
  const pages = pdf.internal.getNumberOfPages();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page);
    pdf.setDrawColor(...theme.primary);
    pdf.setLineWidth(0.4);
    pdf.line(PDF_MARGIN.left, pageHeight - 9, pageWidth - PDF_MARGIN.right, pageHeight - 9);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...theme.muted);
    pdf.text(text, PDF_MARGIN.left, pageHeight - 5);
    pdf.text(`Page ${page} of ${pages}`, pageWidth - PDF_MARGIN.right, pageHeight - 5, {
      align: "right",
    });
  }
  pdf.setTextColor(...theme.text);
};
